import type { Permission } from '@/api/permissions';

export const RoleRequestStatus = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
} as const;

export type RoleRequestStatus = (typeof RoleRequestStatus)[keyof typeof RoleRequestStatus];

export const RoleRequestType = {
  AssignRole: 'assign_role',
  RemoveRole: 'remove_role',
  CreateRole: 'create_role',
  AddRolePermission: 'add_role_permission',
  RemoveRolePermission: 'remove_role_permission',
  DeleteRole: 'delete_role',
  RenameRole: 'rename_role',
} as const;

export type RoleRequestType = (typeof RoleRequestType)[keyof typeof RoleRequestType];

export type AssignRoleRequestInput = {
  userId: string;
  roleId: string;
};

export type RemoveRoleRequestInput = AssignRoleRequestInput;

export type CreateRoleRequestInput = {
  sourceRoleId?: string;
  name: string;
  permissions?: Permission[];
};

export type AddRolePermissionRequestInput = {
  roleId: string;
  permission: Permission;
};

export type RemoveRolePermissionRequestInput = AddRolePermissionRequestInput;

export type DeleteRoleRequestInput = {
  roleId: string;
};

export type RenameRoleRequestInput = {
  roleId: string;
  name: string;
};

export type RoleRequestCreatedResponse = {
  id: string;
  status: Extract<RoleRequestStatus, 'pending' | 'approved'>;
  autoApproved: boolean;
};

export type RoleRequestInputPayloadMap = {
  [RoleRequestType.AssignRole]: AssignRoleRequestInput;
  [RoleRequestType.RemoveRole]: RemoveRoleRequestInput;
  [RoleRequestType.CreateRole]: CreateRoleRequestInput;
  [RoleRequestType.AddRolePermission]: AddRolePermissionRequestInput;
  [RoleRequestType.RemoveRolePermission]: RemoveRolePermissionRequestInput;
  [RoleRequestType.DeleteRole]: DeleteRoleRequestInput;
  [RoleRequestType.RenameRole]: RenameRoleRequestInput;
};

export type RoleRequestInput = {
  [K in RoleRequestType]: { type: K; payload: RoleRequestInputPayloadMap[K] };
}[RoleRequestType];

export type RoleRequestInputPayload = RoleRequestInput['payload'];

export type RoleRequestResponsePayloadMap = {
  [RoleRequestType.AssignRole]: {
    targetUserId: string;
    roleId: string;
    roleName: string;
  };
  [RoleRequestType.RemoveRole]: {
    targetUserId: string;
    roleId: string;
    roleName: string;
  };
  [RoleRequestType.CreateRole]: {
    sourceRoleId: string | null;
    sourceRoleName: string | null;
    name: string;
    resultingPermissions: Permission[];
  };
  [RoleRequestType.AddRolePermission]: {
    roleId: string;
    roleName: string;
    permission: Permission;
  };
  [RoleRequestType.RemoveRolePermission]: {
    roleId: string;
    roleName: string;
    permission: Permission;
  };
  [RoleRequestType.DeleteRole]: {
    roleId: string;
    roleName: string;
  };
  [RoleRequestType.RenameRole]: {
    roleId: string;
    oldName: string;
    newName: string;
  };
};

export type RoleRequestParty = {
  userId: string;
  name: string | null;
  email: string | null;
};

type RoleRequestRecordMetadata = {
  id: string;
  status: RoleRequestStatus;
  initiator: RoleRequestParty;
  /** The member the request is about; null on the types that name a role rather than a member. */
  target: RoleRequestParty | null;
  initiatedAt: string;
  decider: RoleRequestParty | null;
  decidedAt: string | null;
  autoApproved: boolean;
  rejectionReason: string | null;
};

export type RoleRequestRecord = {
  [K in RoleRequestType]: {
    type: K;
    payload: RoleRequestResponsePayloadMap[K];
  } & RoleRequestRecordMetadata;
}[RoleRequestType];

export type EntityRoleRequest = RoleRequestRecord;

export type RoleRequestListResponse = {
  requests: EntityRoleRequest[];
};

export type RoleRequestListParams = {
  status?: RoleRequestStatus;
};

export type RejectRoleRequestBody = {
  reason: string;
};
